import { useState, useEffect } from "react"
import PDFExporter from "../../../Components/PDFExporter"
import Text from "../../../Components/Text"
import { getWorkspace } from "../../../Model/Workspace"
import { getData, saveData } from "../../../db/db_helper"
import Button from "../../../Components/Buttons/Button"

const formatDate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

const formatDateTime = (date) => {
    const d = new Date(date)
    return `${formatDate(d)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

const peekNextRapportNumber = async () => {
    const meta = await getData("rapport_meta")
    return (meta?.lastNumber || 0) + 1
}

const commitNextRapportNumber = async () => {
    const meta = await getData("rapport_meta")
    const lastNumber = meta?.lastNumber || 0
    const nextNumber = lastNumber + 1
    await saveData("rapport_meta", { lastNumber: nextNumber })
    return nextNumber
}

export const GovermentRapport = ({ orders }) => {
    const { exportToPDF } = PDFExporter()
    const [workspace, setWorkspace] = useState(null)
    const [previewNumber, setPreviewNumber] = useState(null)
    const [committedNumber, setCommittedNumber] = useState(null)
    const [isExporting, setIsExporting] = useState(false)
    const [createdAt] = useState(new Date())

    useEffect(() => {
        (async () => {
            setWorkspace(await getWorkspace())
            setPreviewNumber(await peekNextRapportNumber())
        })()
    }, [])

    const handleExport = async () => {
        if (isExporting) return
        setIsExporting(true)

        let numberToUse = committedNumber
        if (numberToUse === null) {
            numberToUse = await commitNextRapportNumber()
            setCommittedNumber(numberToUse)
        }

        await new Promise(resolve => setTimeout(resolve, 0))

        await exportToPDF(
            "rapportContent",
            `mwst-rapport-${numberToUse}-${formatDate(startDate)}-${formatDate(endDate)}.pdf`
        )

        setIsExporting(false)
    }

    const displayNumber = committedNumber ?? previewNumber

    const validOrders = orders.filter(o =>
        o.status !== "CANCELED" &&
        o.status !== "WASTE" &&
        o.paymentType !== "UNPAYED"
    )
    const canceledOrders = orders.filter(o => o.status === "CANCELED")
    const wasteOrders = orders.filter(o => o.status === "WASTE")

    const dates = orders.map(o => new Date(o.startTime))
    const startDate = dates.length ? new Date(Math.min(...dates)) : new Date()
    const endDate = dates.length ? new Date(Math.max(...dates)) : new Date()

    // Fallback-Sätze als volle Prozentzahlen, analog zu product.mwst / product.toGoMwst
    const FALLBACK_ONSITE_VAT = 8.1
    const FALLBACK_TAKEAWAY_VAT = 2.6

    // Ermittelt den korrekten Satz für eine Position, abhängig vom Bestelltyp
    const getProductVatRate = (product, orderType) => {
        if (orderType === "TAKE_AWAY") {
            // TAKE_AWAY: erst toGoMwst, dann mwst, dann Fallback
            if (product.toGomwst != null) return product.toGomwst / 100
            if (product.mwst != null) return product.mwst / 100
            return FALLBACK_TAKEAWAY_VAT / 100
        }

        // ON_SPOT (und alles andere/unbekannte): mwst, sonst Fallback
        if (product.mwst != null) return product.mwst / 100
        return FALLBACK_ONSITE_VAT / 100
    }

    // MWST-Aufschlüsselung nach Satz
    const getVatBreakdown = () => {
        const breakdown = {}

        validOrders.forEach(order => {
            order.orderProducts?.forEach(product => {
                const rate = getProductVatRate(product, order.type)
                const gross = (product.price || 0) * (product.count || 0)
                const net = gross / (1 + rate)
                const vat = gross - net

                const key = rate.toString()
                if (!breakdown[key]) {
                    breakdown[key] = { rate, net: 0, vat: 0, gross: 0 }
                }
                breakdown[key].net += net
                breakdown[key].vat += vat
                breakdown[key].gross += gross
            })
        })

        return Object.values(breakdown).sort((a, b) => b.rate - a.rate)
    }

    const getPaymentLabel = (status) => {
        if (status == null) { return "UNBEKANNT" }
        if (status === "CARD") { return "Karte" }
        if (status === "ON_COUNTER") { return "Bar Bezahlt" }
        if (status === "PAYED_INSIDE") { return "Bar Bezahlt" }
        if (status === "UNPAYED") { return "Unbezahlt" }
    }

    const getPaymentBreakdown = () => {
        const types = {}
        validOrders.forEach(order => {
            const type = getPaymentLabel(order.paymentType)
            if (!types[type]) types[type] = 0
            types[type] += order.totalPrice || 0
        })
        return types
    }

    const vatBreakdown = getVatBreakdown()
    const paymentBreakdown = getPaymentBreakdown()
    const totalGross = validOrders.reduce((sum, o) => sum + (o.totalPrice || 0), 0)
    const totalVat = vatBreakdown.reduce((sum, v) => sum + v.vat, 0)
    const totalNet = totalGross - totalVat

    const currency = workspace?.currency || "CHF"

    return (
        <div className="flex flex-col gap-3">
            <div id="rapportContent" className="bg-white p-6 flex flex-col gap-4 text-sm">

                <div className="flex flex-col gap-1 border-b border-gray-300 pb-3">
                    <Text type="boldbody">
                        MWST-Rapport Nr. {displayNumber ?? "…"}
                    </Text>
                    <Text type="body">{workspace?.name} {workspace?.uuidNumber}</Text>
                    <div className="flex flex-col">
                        <Text type="body">{workspace?.street}</Text>
                        <Text type="body">{workspace?.plz}</Text>
                        <Text type="body">{workspace?.city}</Text>
                    </div>
                    <Text type="body">
                        Zeitraum: {formatDate(startDate)} – {formatDate(endDate)}
                    </Text>
                    <Text type="body">
                        Erstellt am: {formatDateTime(createdAt)}
                    </Text>
                </div>

                <div className="flex flex-col gap-1">
                    <Text type="boldbody">Umsatzübersicht</Text>
                    <div className="flex justify-between">
                        <span>Anzahl Bestellungen</span>
                        <span>{validOrders.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Nettoumsatz</span>
                        <span>{totalNet.toFixed(2)} {currency}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>MWST total</span>
                        <span>{totalVat.toFixed(2)} {currency}</span>
                    </div>
                    <div className="flex justify-between font-bold">
                        <span>Bruttoumsatz</span>
                        <span>{totalGross.toFixed(2)} {currency}</span>
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <Text type="boldbody">MWST-Aufschlüsselung nach Satz</Text>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-300 text-left">
                                <th>Satz</th>
                                <th>Netto</th>
                                <th>MWST</th>
                                <th>Brutto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vatBreakdown.map(v => (
                                <tr key={v.rate}>
                                    <td>{(v.rate * 100).toFixed(1)}%</td>
                                    <td>{v.net.toFixed(2)} {currency}</td>
                                    <td>{v.vat.toFixed(2)} {currency}</td>
                                    <td>{v.gross.toFixed(2)} {currency}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-1">
                    <Text type="boldbody">Umsatz nach Zahlungsart</Text>
                    {Object.entries(paymentBreakdown).map(([type, amount]) => (
                        <div key={type} className="flex justify-between">
                            <span>{type}</span>
                            <span>{amount.toFixed(2)} {currency}</span>
                        </div>
                    ))}
                </div>

                {canceledOrders.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <Text type="boldbody">Stornierte Bestellungen ({canceledOrders.length})</Text>
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-300 text-left">
                                    <th>Zeit</th>
                                    <th>Betrag</th>
                                    <th>Grund</th>
                                </tr>
                            </thead>
                            <tbody>
                                {canceledOrders.map(o => (
                                    <tr key={o.id}>
                                        <td>{formatDateTime(o.startTime)}</td>
                                        <td>{(o.totalPrice || 0).toFixed(2)} {currency}</td>
                                        <td>{o.cancelReason || "–"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {wasteOrders.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <Text type="boldbody">Waste ({wasteOrders.length})</Text>
                        <Text type="body">
                            {wasteOrders.length} Bestellungen als Waste markiert, nicht im Umsatz enthalten.
                        </Text>
                    </div>
                )}

                <div className="text-xs text-gray-400 mt-4 border-t border-gray-300 pt-2">
                    Rapport Nr. {displayNumber} — fortlaufend, nicht editierbar nach Erstellung.
                </div>
            </div>

            <Button
                text={isExporting ? "Exportiere..." : "Erstellen und als PDF Exportieren"}
                onClickAction={handleExport}
            />
        </div>
    )
}